import PropTypes from 'prop-types';
import { useEffect, Fragment } from 'react';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Spinner } from '@nike/eds';
import { useInView } from 'react-intersection-observer';
import RecommendConceptsClient from '../../clientAPI/recommendConcepts';
import { QUERY_KEYS } from '../../constants';
import ConceptView from './ConceptView';

export default function RecommendConceptsTreeView({
  conceptType,
}: {
  conceptType: string;
}) {
  const { ref, inView } = useInView({ delay: 1000 });
  const revalidator = useRevalidator();
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.RECOMMEND_CONCEPT_LIST, conceptType],
    queryFn: async ({ pageParam }) => {
      if (pageParam) {
        return RecommendConceptsClient.next(pageParam);
      } else {
        const whereDSL =
          conceptType === 'product_feed/static_collections'
            ? `parentId==2d375fdb-39a1-4982-842f-3f4a357ec32a;source.system==${conceptType}`
            : `parentId==null;source.system==${conceptType}`;
        return RecommendConceptsClient.getConceptsByWhereCause(whereDSL);
      }
    },
    getNextPageParam: (lastPage, pages) => lastPage.pages.next,
    staleTime: Infinity,
    suspense: true,
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView]);

  return (
    <div>
      <ul>
        {data &&
          data.pages.map((page) => (
            <Fragment
              key={
                page.pages.next ? page.pages.next : 'root_concept_viewer_page_1'
              }
            >
              {page.objects.map((rw, idx) => {
                const id = `root_concept_viewer_${rw.id}`;
                const key = `root-${rw.id}`;
                return (
                  <li key={key}>
                    <ConceptView id={id} data={rw} loadChildren={true} />
                  </li>
                );
              })}
            </Fragment>
          ))}
      </ul>
      <div className='tw-w-full tw-h-10 tw-mx-4 tw-text-center' ref={ref}>
        {isFetchingNextPage ? <Spinner className='tw-ml-12 tw-my-2' /> : <></>}
      </div>
    </div>
  );
}

RecommendConceptsTreeView.propTypes = {
  conceptType: PropTypes.string,
};
